// react
import { ChangeEvent, useContext, useState } from 'react';

// i81n
import { useTranslation } from 'react-i18next';

// types
import { Permission, PermissionOperation, PermissionResource, Permissions } from '../../../@types';
import { permissionResourcesV2 } from '../../../@types/unions';

// context
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';
import { usePermissions } from '../../../hooks/usePermissions';

// mui
import { Grid, InputLabel, SelectChangeEvent } from '@mui/material';

// fields
import { Select } from '../../fields/Select/Select';

// tables
import { TableBasicPermissions } from '../../tables/basic/TableBasicPermissions/TableBasicPermissions';

// utils
import {
	ConstructPermissionsProps,
	permissionOperationMap,
	permissionResourceMap,
	permissionsReadWrite,
} from '../../../utils/permissions';
import { translatePermissionResource } from '../../../utils/translations';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export type PermissionsTemplate = 'CUSTOM' | 'READ_ONLY' | 'READ_WRITE';

export interface ValuesProps {
	permissions: Permissions;
	template: PermissionsTemplate;
}

export interface UserPermissionsProps {
	defaultPermissions?: Permissions;
	defaultTemplate?: PermissionsTemplate;
	onChange: (e: ChangeEvent | SelectChangeEvent<unknown>, permissions: Permissions) => void;
}

export const UserPermissions = ({
	defaultPermissions = permissionsReadWrite,
	defaultTemplate = 'READ_WRITE',
	onChange,
}: UserPermissionsProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { project } = useContext(ContextProject);

	// hooks
	const { t } = useTranslation();
	const { getPermissionsDefault } = usePermissions();

	// state
	const [permissions, setPermissions] = useState<Permission[]>([]);
	const [values, setValues] = useState<ValuesProps>({
		permissions: defaultPermissions,
		template: defaultTemplate,
	});

	// fetch permissions
	useFetch({
		url: `${process.env.REACT_APP_API_URL}/permissions`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				let permissionsNew: Permission[] = res.data;
				if (!featureFlags?.ACTIVITIES) {
					permissionsNew = permissionsNew.filter((p) => p.resource !== 'ACT');
				}
				setPermissions(permissionsNew);
			}
		},
	});

	let resources = [...new Set(permissions.map((p) => p.resource))].sort((a, b) => (a > b ? 1 : -1));

	if (project?.version === '1') {
		// filter out v2 resources
		resources = resources.filter((r) => !permissionResourcesV2.includes(r));
	}

	const permissionsByResource = resources
		.map((resource) => {
			const resourcePermissions = permissions.filter((p) => p.resource === resource);

			return {
				label: translatePermissionResource(resource, t),
				resource,
				permissions: resourcePermissions,
			};
		})
		.sort((a, b) => {
			return a.label.localeCompare(b.label);
		});

	const handleChangePermissionsTemplate = (e: SelectChangeEvent<unknown>) => {
		const { value } = e.target as HTMLInputElement;

		if (value === 'CUSTOM') {
			setValues((prev) => ({ ...prev, template: 'CUSTOM' }));
			return;
		}

		const permissionsNew = getPermissionsDefault({
			template: value as ConstructPermissionsProps['template'],
		});

		onChange(e, permissionsNew);

		setValues((prev) => ({
			...prev,
			permissions: permissionsNew,
			template: value as PermissionsTemplate,
		}));
	};

	const handleChangePermission = (
		e: ChangeEvent,
		resource: PermissionResource,
		operation: PermissionOperation
	) => {
		const { checked } = e.target as HTMLInputElement;

		const valuesNew = {
			...values,
			permissions: {
				...values.permissions,
				[permissionResourceMap[resource]]: {
					...values.permissions[permissionResourceMap[resource]],
					[permissionOperationMap[operation]]: checked,
				},
			},
		};

		onChange(e, valuesNew.permissions);

		setValues(valuesNew);
	};

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Grid alignItems="flex-end" container justifyContent="space-between" spacing={2}>
					<Grid item>
						<InputLabel required={true}>{t('permissions')}</InputLabel>
					</Grid>
					<Grid item>
						<Select
							hasNoneOption={false}
							name="permissionsTemplate"
							onChange={handleChangePermissionsTemplate}
							options={[
								{
									label: t('custom'),
									value: 'CUSTOM',
								},
								{
									label: t('readAndWrite'),
									value: 'READ_WRITE',
								},
								{
									label: t('readOnly'),
									value: 'READ_ONLY',
								},
							]}
							size="small"
							value={values.template}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12}>
				<TableBasicPermissions
					data={permissionsByResource}
					onChange={handleChangePermission}
					values={values.permissions}
				/>
			</Grid>
		</Grid>
	);
};
